import { ReactNode, useMemo } from 'react';
import { Source } from 'shared/map-exports';
import { useQuery } from '@tanstack/react-query';
import emptyStyleUrl from 'assets/empty-style.json?url';
import { AuthenticatedTileAPI } from 'features/mapping/hooks/useAuthenticatedTileRequestTransformer';

type VisibilityToggledSourceProps = {
  children: ReactNode;
  url: string;
  visible: boolean;
  promoteId?: string;
  volatile?: boolean;
  refreshIntervalSeconds?: number;
  id?: string;
};

const getTileJsonLastModified = async (url: string): Promise<string> => {
  const response = await AuthenticatedTileAPI.head(url);
  const lastModified = response.headers['last-modified'];
  if (lastModified) return lastModified;
  return '';
};

export const VisibilityToggledSource = (
  props: VisibilityToggledSourceProps,
): JSX.Element | null => {
  const {
    children,
    url,
    visible,
    promoteId,
    volatile = false,
    refreshIntervalSeconds,
    id,
  } = props;

  const isRefetchEnabled = useMemo(
    () =>
      visible &&
      refreshIntervalSeconds !== undefined &&
      refreshIntervalSeconds > 0,
    [visible, refreshIntervalSeconds],
  );

  const { data: tilesetLastModified = '' } = useQuery({
    queryKey: ['tileJsonLastModified', url],
    enabled: isRefetchEnabled,
    queryFn: async () => getTileJsonLastModified(url),
    refetchInterval: refreshIntervalSeconds && refreshIntervalSeconds * 1000,
  });

  const sourceUrl = useMemo(() => {
    if (!visible) {
      return emptyStyleUrl;
    }
    if (isRefetchEnabled) {
      const separator = url.includes('?') ? '&' : '?'; // the URL may already have a query string
      return `${url}${separator}ignoredForceLocalRefresh=${encodeURIComponent(tilesetLastModified)}`;
    }
    return url;
  }, [visible, isRefetchEnabled, url, tilesetLastModified]);

  return (
    <Source
      url={sourceUrl}
      type="vector"
      promoteId={promoteId}
      volatile={volatile}
      id={id}
    >
      {children}
    </Source>
  );
};
